import { detectOverflow, type Modifier } from '@popperjs/core';

// Popper sizes a dropdown to its content and never tells it how much room it
// actually had, so a planner with a dozen categories grew a filter card taller
// than the screen with no way to reach the bottom of it. This measures what is
// left after placement and publishes it as a custom property the card caps
// itself with. A variable rather than a plain `maxHeight` because popper styles
// the positioning wrapper, not the card inside it, and custom properties
// inherit down to it.
export const AVAILABLE_HEIGHT_VAR = '--ah-popper-available-height';

// Keeps the card off the very edge of the screen.
const VIEWPORT_PADDING = 12;

// Below this a scroll area is too cramped to read. Flip and preventOverflow
// should have moved the card long before it gets this tight, so treat the floor
// as a backstop rather than a size we expect to hand out.
const MIN_HEIGHT = 160;

export const availableHeightModifier: Modifier<'ahAvailableHeight', object> = {
  name: 'ahAvailableHeight',
  enabled: true,
  phase: 'beforeWrite',
  // Only the room left once the popper has been offset, flipped and shifted is
  // real, so measure after those have had their say.
  requiresIfExists: ['offset', 'preventOverflow', 'flip'],
  fn: ({ state }) => {
    const overflow = detectOverflow(state, { padding: VIEWPORT_PADDING });
    const [basePlacement] = state.placement.split('-');
    // A flipped card is anchored by its bottom edge, so the room it can grow
    // into runs up towards the top of the screen instead.
    const edge = basePlacement === 'top' ? 'top' : 'bottom';
    // detectOverflow re-derives the position from the raw placement, so it
    // misses any shift preventOverflow applied and that has to be added back. A
    // positive y moves the card down the page, which costs it room below and
    // buys it the same room above, so the sign follows the edge being measured.
    // Zero as things stand, since Chakra leaves preventOverflow's altAxis off.
    const { y = 0 } = state.modifiersData.preventOverflow ?? {};
    const shift = edge === 'bottom' ? -y : y;
    const available = state.rects.popper.height - overflow[edge] + shift;

    state.elements.popper.style.setProperty(
      AVAILABLE_HEIGHT_VAR,
      `${Math.max(available, MIN_HEIGHT)}px`
    );
  },
};

// usePopper rebuilds its instance whenever this identity changes, so it has to
// be shared rather than built inline.
export const availableHeightModifiers = [availableHeightModifier];
